import { useCallback, useSyncExternalStore } from 'react';

/**
 * Site theme: one source of truth, held in localStorage and mirrored onto the
 * document element as `data-theme` / `data-mode`.
 *
 * The colours themselves live in CSS -- `:root[data-theme="..."]` in
 * index.css redefines `--primary` / `--accent`, and `[data-mode="dark"]`
 * flips the surface tokens. That is what makes a change take effect
 * immediately: swapping an attribute on <html> repaints every consumer of
 * those custom properties, with no React re-render and no reload.
 *
 * `useTheme()` exists on top of that for components that need to *read* the
 * current selection (the Customize panel's checkmarks, say). It subscribes to
 * a small store so every consumer stays in step.
 */

export const SITE_THEME_STORAGE_KEY = 'app_site_theme';

export const THEME_PALETTE_IDS = [
  'default',
  'slate',
  'red',
  'rose',
  'brown',
  'green',
  'yellow',
] as const;

export const THEME_MODE_IDS = ['light', 'dark'] as const;

export type ThemePalette = (typeof THEME_PALETTE_IDS)[number];
export type ThemeMode = (typeof THEME_MODE_IDS)[number];

export interface SiteTheme {
  palette: ThemePalette;
  mode: ThemeMode;
}

/** What an unset, unreadable or malformed stored value falls back to. */
export const DEFAULT_SITE_THEME: SiteTheme = { palette: 'default', mode: 'light' };

function isPalette(value: unknown): value is ThemePalette {
  return THEME_PALETTE_IDS.includes(value as ThemePalette);
}

function isMode(value: unknown): value is ThemeMode {
  return THEME_MODE_IDS.includes(value as ThemeMode);
}

/**
 * Storage is guarded everywhere: Safari private mode throws on write, and
 * storage can be disabled outright. The theme then lives for the current page
 * load only, which is a better outcome than a crash on boot.
 */
function storage(): Storage | null {
  try {
    const probe = '__niraxobid_theme_probe__';
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads the stored theme, validating both halves independently: a payload with
 * a good mode and a junk palette keeps the mode rather than resetting both.
 */
export function readStoredTheme(): SiteTheme {
  try {
    const raw = storage()?.getItem(SITE_THEME_STORAGE_KEY);
    if (!raw) return DEFAULT_SITE_THEME;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SITE_THEME;

    const candidate = parsed as Partial<SiteTheme>;
    return {
      palette: isPalette(candidate.palette) ? candidate.palette : DEFAULT_SITE_THEME.palette,
      mode: isMode(candidate.mode) ? candidate.mode : DEFAULT_SITE_THEME.mode,
    };
  } catch {
    // Unreadable or non-JSON: treat it as unset rather than propagating.
    return DEFAULT_SITE_THEME;
  }
}

/** Mirrors the theme onto <html>, which is what the CSS selectors key off. */
function writeToDocument(theme: SiteTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme.palette;
  document.documentElement.dataset.mode = theme.mode;
}

/* -- the store ------------------------------------------------------------- */

let current: SiteTheme = DEFAULT_SITE_THEME;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/*
  useSyncExternalStore compares snapshots by identity, so this must return the
  same object until the theme actually changes -- returning a fresh literal
  would loop forever.
*/
function getSnapshot(): SiteTheme {
  return current;
}

/**
 * Persists the theme and applies it in one call.
 *
 * Safe to call before `initTheme` -- it writes the document attributes itself
 * rather than assuming they are already there.
 */
export function applyTheme(theme: SiteTheme): void {
  current = { palette: theme.palette, mode: theme.mode };

  try {
    storage()?.setItem(SITE_THEME_STORAGE_KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable -- the theme still applies for this page load */
  }

  writeToDocument(current);
  listeners.forEach((listener) => {
    listener();
  });
}

/**
 * App start-up: adopt whatever was stored and put it on the document.
 *
 * Called from main.tsx before the first render rather than from an effect, so
 * the first paint is already themed instead of flashing the default palette.
 */
export function initTheme(): SiteTheme {
  current = readStoredTheme();
  writeToDocument(current);
  return current;
}

/**
 * Sign-out drops the stored theme: it is organization-scoped, and the next
 * person to use this browser must start from the platform default until the
 * organization's own payload supplies its theme again (the login page fetches
 * it the moment it renders).
 *
 * The in-memory snapshot and the document attributes go with the key, so the
 * page that is still on screen repaints immediately instead of keeping a theme
 * that is no longer backed by anything.
 */
export function clearStoredTheme(): void {
  current = DEFAULT_SITE_THEME;

  try {
    storage()?.removeItem(SITE_THEME_STORAGE_KEY);
  } catch {
    /* storage unavailable -- the reset below is all there is to do */
  }

  writeToDocument(current);
  listeners.forEach((listener) => {
    listener();
  });
}

/** The live theme, plus the setter. Re-renders every consumer on change. */
export function useTheme(): { theme: SiteTheme; applyTheme: (theme: SiteTheme) => void } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const set = useCallback((next: SiteTheme) => {
    applyTheme(next);
  }, []);

  return { theme, applyTheme: set };
}

/** Test-only: drops the in-memory snapshot back to the default. */
export function resetThemeForTests(): void {
  current = DEFAULT_SITE_THEME;
  listeners.clear();
}
